import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLinkedin, FaFacebook, FaXTwitter } from 'react-icons/fa6';

const footerLinks = {
  Products: [
    { label: 'Clutch Logistics', to: '/logistics' },
    { label: 'Clutch Bookings', to: '/bookings' },
    { label: 'AI for SMBs', to: '/services' },
    { label: 'AI Solutions', to: '/services' },
    { label: 'Solutions Overview', to: '/services' },
  ],
  Company: [
    { label: 'About Us', to: '/about' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Contact', to: '/contact' },
  ],
  Support: [
    { label: 'Help Centre', to: '/support' },
    { label: 'Sign In', href: 'https://go.clutchit.com.au/auth/signin' },
    { label: 'Get Started', to: '/pricing' },
  ],
  Legal: [
    { label: 'Terms of Use', to: '/terms-of-use' },
    { label: 'Privacy Policy', to: '/privacy-policy' },
  ],
};

const Footer = () => {
  return (
    <footer className="bg-surface-1 border-t border-border-subtle relative overflow-hidden">
      {/* Subtle top accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[1px] bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
      <div className="absolute inset-0 bg-mesh-grid pointer-events-none opacity-60" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Main footer grid */}
        <div className="py-16 grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1 flex flex-col">
            <Link to="/" className="inline-block mb-4 hover:opacity-75 transition-opacity">
              <img src="/images/clutch_blue_1.png" alt="Clutch" className="h-9" />
            </Link>
            <p className="text-secondary text-sm leading-relaxed mb-6">
              Purpose-built logistics technology for Australia's agricultural supply chain.
            </p>
            <div className="flex items-center gap-3 mt-auto">
              {[
                { icon: <FaLinkedin size={15} />, href: 'https://linkedin.com', label: 'LinkedIn' },
                { icon: <FaFacebook size={15} />, href: 'https://facebook.com', label: 'Facebook' },
                { icon: <FaXTwitter size={15} />, href: 'https://twitter.com', label: 'X/Twitter' },
              ].map(({ icon, href, label }) => (
                <motion.a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-8 h-8 rounded-lg bg-white border border-border-subtle shadow-sm flex items-center justify-center text-secondary hover:text-accent hover:border-accent/30 transition-colors duration-200 cursor-pointer"
                >
                  {icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([heading, links]) => (
            <div key={heading}>
              <h4 className="text-xs font-semibold text-primary tracking-widest uppercase mb-4">{heading}</h4>
              <ul className="space-y-3">
                {links.map(({ label, to, href }) => (
                  <li key={label}>
                    {to ? (
                      <Link
                        to={to}
                        className="text-secondary text-sm hover:text-accent transition-colors duration-200 cursor-pointer"
                      >
                        {label}
                      </Link>
                    ) : (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary text-sm hover:text-accent transition-colors duration-200 cursor-pointer"
                      >
                        {label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Address & contact bar */}
        <div className="py-6 border-t border-border-subtle flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-secondary text-sm">
            <svg className="w-4 h-4 text-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
            115–117 William Angliss Drive, Laverton North, Victoria, 3026
          </div>
          <div className="flex items-center gap-2 text-secondary text-sm">
            <svg className="w-4 h-4 text-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
            </svg>
            +61 438 925 410
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-6 border-t border-border-subtle flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-secondary text-sm">
            © {new Date().getFullYear()} Clutch IT Solutions. All rights reserved.
          </p>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-secondary text-xs">All systems operational</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
